import type { ChatOpenOptions, ChatPlugin } from '#types/plugin/chat'
import livechatConfig from '#brand/config/integrations/livechat'
import agentforceChat from '#core/integrations/agentforce-chat'
import clarityAiChat from '#core/integrations/clarity-ai'
import enhancedLiveChat from '#core/integrations/enhanced-live-chat'
import sierraChat from '#core/integrations/sierra-chat'

/**
 * Resolves feature flags when used so asynchronous Optimizely overrides
 * can switch providers without replacing the injection.
 * Provider instances are created on demand and cached for the app lifetime.
 */
export default defineNuxtPlugin<{ chat: ChatPlugin }>(() => {
  const nuxtApp = useNuxtApp()
  const featureFlags = useFeatureFlags()
  const locale = useLocale()
  const isAgentforceOpen = useState('agentforceOpen', () => false)
  const providerScope = effectScope(true)

  nuxtApp.vueApp.onUnmount(() => providerScope.stop())

  let sierraProvider: ChatPlugin | undefined
  const factories = {
    agentforce: (): ChatPlugin => {
      const provider = agentforceChat()
      return {
        ...provider,
        open: (input?: string | ChatOpenOptions) => {
          if (!isAgentforceOpen.value && input && typeof input === 'object')
            nuxtApp.$gtm.push('agentforce.onInitialize', input.eventLabel, input.buttonLocation)
          provider.open()
        }
      }
    },
    disabled: (): ChatPlugin => ({
      open: () => log.warn('Chat is disabled by feature flag'),
      available: ref(false)
    }),
    none: (): ChatPlugin => ({
      open: () => log.warn('Chat is not implemented.'),
      available: ref(false)
    }),
    clarity: (): ChatPlugin => {
      const clarity = clarityAiChat()
      const sierra = sierraProvider ??= sierraChat()
      return {
        open: (query, context) =>
          window.CLARITY_HELPER
            ? clarity.open(query, context)
            : sierra.open(query, context),
        available: computed(() => !!(clarity.available?.value || sierra.available?.value)),
        amaAvailable: clarity.available
      }
    },
    sierra: () => sierraProvider ??= sierraChat(),
    enhanced: () => enhancedLiveChat(livechatConfig)
  }

  type ProviderKey = keyof typeof factories
  const providerKey = computed<ProviderKey>(() => {
    if (featureFlags.enableSfccAgentforceChat) return 'agentforce'
    if (!featureFlags.enableChat) return 'disabled'

    const { configLivechat, tempAskMeAnythingSearchHub } = featureFlags
    if (!configLivechat.enabled) return 'none'
    if (configLivechat.clarityRegions?.includes(locale) && tempAskMeAnythingSearchHub) return 'clarity'
    if (configLivechat.sierraRegions?.includes(locale) && !tempAskMeAnythingSearchHub) return 'sierra'
    return 'enhanced'
  })

  const providers: Partial<Record<ProviderKey, ChatPlugin>> = {}
  // Keep deferred flag reads and integration setup in Nuxt context and out of
  // a caller's component scope, so cached watchers survive navigation.
  // runWithContext is synchronous on the client; Nuxt's type also includes SSR promises.
  const currentProvider = computed(() => providerScope.run(() => nuxtApp.runWithContext(() => {
    const key = providerKey.value
    return providers[key] ??= factories[key]()
  }) as ChatPlugin)!)
  const launcher = computed(() => currentProvider.value.launcher)

  const chat: ChatPlugin = {
    open: (query, context) => currentProvider.value.open(query, context),
    available: computed(() => currentProvider.value.available?.value ?? false),
    amaAvailable: computed(() => currentProvider.value.amaAvailable?.value ?? false),
    get launcher() { return launcher.value }
  }

  return {
    provide: {
      chat
    }
  }
})
